import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SharedService } from 'src/app/Services/sharedService';
import { getEmployeeListStatusType } from 'src/app/utils/employee-status.util';

export type StatusTabValue =
  | 'all'
  | 'online'
  | 'idle'
  | 'offline'
  | 'absent'
  | 'invited'
  | 'auto-logout';

@Component({
  selector: 'user-list-filter',
  templateUrl: './user-list-filter.component.html',
  styleUrl: './user-list-filter.component.css',
  standalone: false
})
export class UserListFilterComponent implements OnInit {
  readonly tabs: { label: string; value: StatusTabValue }[] = [
    { label: 'All', value: 'all' },
    { label: 'Online', value: 'online' },
    { label: 'Idle', value: 'idle' },
    { label: 'Offline', value: 'offline' },
    { label: 'Absent', value: 'absent' },
    { label: 'Invited', value: 'invited' },
    { label: 'Auto Logout', value: 'auto-logout' },
  ];

  selectedValue: StatusTabValue = 'all';
  tabCounts: Record<StatusTabValue, number> = {
    all: 0,
    online: 0,
    idle: 0,
    offline: 0,
    absent: 0,
    invited: 0,
    'auto-logout': 0,
  };

  private bootstrapped = false;
  private _employees: any[] = [];
  private _statusEpoch = 0;

  @Input() set employees(value: any[] | null | undefined) {
    this._employees = Array.isArray(value) ? value : [];
    this.recomputeCounts();
  }

  /** Bumped by parent when Firebase mutates statuses in place (same array ref). */
  @Input() set statusEpoch(value: number) {
    const next = Number(value) || 0;
    if (next === this._statusEpoch) {
      return;
    }
    this._statusEpoch = next;
    this.recomputeCounts();
  }

  @Input('queryFiltered') queryFiltered: any;
  @Output() statusChanges = new EventEmitter<{ label: string; value: StatusTabValue }[]>();

  constructor(
    private route: ActivatedRoute,
    private shared: SharedService
  ) {}

  ngOnInit(): void {
    if (this.bootstrapped) {
      return;
    }
    this.bootstrapped = true;

    const raw = this.route.snapshot.queryParamMap.get('filter') || '';
    this.selectedValue = this.mapFilter(raw);
    // Single emit — parent writes URL; we do NOT re-subscribe in a way that re-emits
    this.statusChanges.emit([
      { label: this.tabs.find((t) => t.value === this.selectedValue)?.label || 'All', value: this.selectedValue }
    ]);
  }

  selectTab(value: StatusTabValue): void {
    if (value === this.selectedValue) {
      return;
    }
    this.selectedValue = value;
    this.statusChanges.emit([
      { label: this.tabs.find((t) => t.value === value)?.label || value, value }
    ]);
  }

  /** Always show every status tab so the bar stays stable (including zero counts). */
  get visibleTabs(): { label: string; value: StatusTabValue }[] {
    return this.tabs;
  }

  private recomputeCounts(): void {
    const counts: Record<StatusTabValue, number> = {
      all: this._employees.length,
      online: 0,
      idle: 0,
      offline: 0,
      absent: 0,
      invited: 0,
      'auto-logout': 0,
    };
    const dayDiff = (s: unknown) => this.shared.getDayDifferenceForAbsent(s);
    for (const user of this._employees) {
      const type = getEmployeeListStatusType(user, dayDiff);
      if (type === 'none') {
        continue;
      }
      counts[type] += 1;
    }
    this.tabCounts = counts;
  }

  private mapFilter(raw: string): StatusTabValue {
    if (!raw) {
      return 'all';
    }
    const parts = raw.split('_').filter(Boolean);
    // Legacy "Online & Idle" / active bookmarks → All
    if (
      parts.includes('active') ||
      (parts.length === 2 && parts.includes('online') && parts.includes('idle'))
    ) {
      return 'all';
    }
    if (parts.length === 1 && this.tabs.some((t) => t.value === parts[0])) {
      return parts[0] as StatusTabValue;
    }
    return 'all';
  }
}
